import type {
  AffiliateAddress,
  AffiliateContact,
  AffiliatePlan,
  AffiliatePortalViewConfig,
  AffiliatePortalViewConfigColumn,
  AffiliatePortalViewConfigKpi,
  AffiliatePromoCodePayload,
  CpaActionType,
  PatchAffiliateRequest,
  RevenueSource,
} from "../../types/affiliates";
import type { ContactInformationFormValues } from "../../components/Affiliates/NewAffiliate/ContactInformation/contactInformationData";
import {
  DEFAULT_SELECTED_COLUMNS,
  KPI_ELIGIBLE_IDS,
} from "../../components/Affiliates/NewAffiliate/AffiliatePamAccount/affiliatePamAccountData";
import type {
  AffiliatePamAccountFormValues,
  ShareableColumnId,
} from "../../components/Affiliates/NewAffiliate/AffiliatePamAccount/affiliatePamAccountData";
import type {
  AndroidIfNotInstalled,
  AndroidLinkBehavior,
  CreatePromoCodeFormValues,
  IosIfNotInstalled,
  IosLinkBehavior,
} from "../../components/Affiliates/NewAffiliate/PromoCodes/CreatePromoCodeModal/createPromoCodeModalData";
import { buildPromoCodeSlug } from "../../components/Affiliates/NewAffiliate/PromoCodes/promoCodesData";
import type { PaymentStepValue } from "../../components/Affiliates/NewAffiliate/Payment/Payment";

const trimOrNull = (raw: string | undefined | null): string | null => {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length === 0 ? null : trimmed;
};

const buildAddress = (
  values: Partial<ContactInformationFormValues>,
): AffiliateAddress => ({
  line1: trimOrNull(values.addressLine1),
  line2: trimOrNull(values.addressLine2),
  city: trimOrNull(values.city),
  state: trimOrNull(values.state),
  zip: trimOrNull(values.zipCode),
  // Defaulted to US — the state dropdown only carries US states for now.
  country: "US",
});

// The edit-info modal only exposes a single name input, so we only PATCH
// `contact_name`. Sending `company_name` here would overwrite a distinct
// value with the same string the operator never saw — silent data
// loss the first time anyone hits Save. When/if the form splits the two
// fields, add `company_name` back as its own key.
export const mapContactToPatchPayload = (
  values: Partial<ContactInformationFormValues>,
): Pick<
  PatchAffiliateRequest,
  "contact_name" | "phone" | "website_url" | "address"
> => ({
  contact_name: trimOrNull(values.name),
  phone: trimOrNull(values.phone),
  website_url: trimOrNull(values.websiteUrl),
  address: buildAddress(values),
});

// Single `name` field maps into both `company_name` and `contact_name`
// until the form splits them.
export const mapContactToPayload = (
  values: Partial<ContactInformationFormValues>,
): AffiliateContact => {
  const email = trimOrNull(values.email);
  const name = trimOrNull(values.name);
  if (!email) {
    throw new Error("contact.email is required to create an affiliate");
  }
  return {
    email,
    company_name: name,
    contact_name: name,
    phone: trimOrNull(values.phone),
    website_url: trimOrNull(values.websiteUrl),
    address: buildAddress(values),
  };
};

const toFiniteNumber = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
};

type CpaFields = Pick<
  AffiliatePlan,
  "cpa_action_type" | "cpa_payment_per_action"
>;

type RevShareFields = Pick<
  AffiliatePlan,
  | "revenue_source"
  | "revenue_share_pct_sportsbook"
  | "min_sportsbook_profit_threshold"
  | "revenue_share_pct_casino"
  | "min_casino_profit_threshold"
>;

const buildCpaFields = (
  cpa: PaymentStepValue["cpa"],
): CpaFields | null => {
  const action = cpa?.actionType;
  const amount = toFiniteNumber(cpa?.paymentPerAction);
  if (!action || amount === null) return null;
  return {
    cpa_action_type: action as CpaActionType,
    cpa_payment_per_action: amount,
  };
};

const buildRevShareFields = (
  rs: PaymentStepValue["revShare"],
): RevShareFields | null => {
  if (!rs?.revenueSource) return null;
  const source = rs.revenueSource;

  const usesSportsbook =
    source === "sportsbook" || source === "sportsbook_casino";
  const usesCasino = source === "casino" || source === "sportsbook_casino";

  const sbPct = usesSportsbook
    ? toFiniteNumber(rs.sportsbook?.revenuePct)
    : null;
  const sbMin = usesSportsbook
    ? toFiniteNumber(rs.sportsbook?.minProfit)
    : null;
  const csPct = usesCasino ? toFiniteNumber(rs.casino?.revenuePct) : null;
  const csMin = usesCasino ? toFiniteNumber(rs.casino?.minProfit) : null;

  if (usesSportsbook && sbPct === null) return null;
  if (usesCasino && csPct === null) return null;

  return {
    revenue_source: source as RevenueSource,
    revenue_share_pct_sportsbook: sbPct,
    min_sportsbook_profit_threshold: sbMin,
    revenue_share_pct_casino: csPct,
    min_casino_profit_threshold: csMin,
  };
};

const EMPTY_CPA_FIELDS: CpaFields = {
  cpa_action_type: null,
  cpa_payment_per_action: null,
};

const EMPTY_REV_SHARE_FIELDS: RevShareFields = {
  revenue_source: null,
  revenue_share_pct_sportsbook: null,
  min_sportsbook_profit_threshold: null,
  revenue_share_pct_casino: null,
  min_casino_profit_threshold: null,
};

export const mapPaymentToPayload = (
  values: Partial<PaymentStepValue> | undefined,
): AffiliatePlan | null => {
  if (!values?.type) return null;

  if (values.type === "cpa") {
    const cpa = buildCpaFields(values.cpa);
    if (!cpa) return null;
    return { plan_type: "CPA", ...cpa, ...EMPTY_REV_SHARE_FIELDS };
  }

  if (values.type === "rev_share") {
    const rs = buildRevShareFields(values.revShare);
    if (!rs) return null;
    return { plan_type: "REVENUE_SHARE", ...EMPTY_CPA_FIELDS, ...rs };
  }

  if (values.type === "hybrid") {
    const cpa = buildCpaFields(values.cpa);
    const rs = buildRevShareFields(values.revShare);
    if (!cpa || !rs) return null;
    return { plan_type: "HYBRID", ...cpa, ...rs };
  }

  return null;
};

// PAM radios are `app` | `browser`; AOA/BE wire values are
// `open_app` | `open_browser`. Unknown values default to open_app on
// BE — mapping here is what makes the "open in browser" radio actually
// stick.
const mapLinkBehavior = (
  behavior: IosLinkBehavior | AndroidLinkBehavior | undefined,
): "open_app" | "open_browser" | null => {
  if (behavior === "app") return "open_app";
  if (behavior === "browser") return "open_browser";
  return null;
};

// AOA: JSON null → native store listing; any string → Custom URL.
const mapFallbackUrl = (
  createAppsOnAirLink: boolean,
  ifNotInstalled: IosIfNotInstalled | AndroidIfNotInstalled | undefined,
  customUrl: string | undefined,
): string | null => {
  if (!createAppsOnAirLink || ifNotInstalled !== "custom_url") return null;
  return trimOrNull(customUrl);
};

const KPI_ID_TO_PORTAL_VIEW_CONFIG: Record<
  ShareableColumnId,
  AffiliatePortalViewConfigKpi | AffiliatePortalViewConfigColumn
> = {
  referralCode: "REFERRAL_CODE",
  totalUsers: "TOTAL_USERS",
  totalFtds: "TOTAL_FTDS",
  ftdRate: "FTD_RATE",
  cpaAmount: "CPA_AMOUNT",
  sportsbookPnl: "SPORTSBOOK_PL",
  // BE still labels this NGR even though the FE now renders "Casino GGR".
  // Keep the wire literal in sync with BE — they own the rename schedule.
  casinoNgr: "CASINO_NGR",
  totalOwed: "TOTAL_OWED",
  totalRevenue: "TOTAL_REVENUE",
  status: "DISABLED",
};

export const mapPortalViewConfigToPayload = (
  pamAccount: AffiliatePamAccountFormValues | undefined,
): AffiliatePortalViewConfig => {
  const selectedColumns = pamAccount?.selectedColumns ?? [];
  // Widen the literal-tuple KPI list back to the full id union so TS
  // doesn't narrow `.includes` to just the 5 KPI ids.
  const kpiIds = KPI_ELIGIBLE_IDS as readonly ShareableColumnId[];
  const kpi_cards: AffiliatePortalViewConfigKpi[] = [];
  const table_columns: AffiliatePortalViewConfigColumn[] = [];
  selectedColumns.forEach((id) => {
    const mapped = KPI_ID_TO_PORTAL_VIEW_CONFIG[id];
    if (kpiIds.includes(id)) {
      kpi_cards.push(mapped as AffiliatePortalViewConfigKpi);
    } else {
      table_columns.push(mapped as AffiliatePortalViewConfigColumn);
    }
  });
  return { kpi_cards, table_columns };
};

// Inverse of `KPI_ID_TO_PORTAL_VIEW_CONFIG` (UPPER_SNAKE BE id → camelCase
// FE id). Derived so the forward map stays the only source of truth.
const KPI_ID_FROM_PORTAL_VIEW_CONFIG: Record<
  AffiliatePortalViewConfigKpi | AffiliatePortalViewConfigColumn,
  ShareableColumnId
> = Object.entries(KPI_ID_TO_PORTAL_VIEW_CONFIG).reduce(
  (acc, [camel, upperSnake]) => {
    acc[
      upperSnake as AffiliatePortalViewConfigKpi | AffiliatePortalViewConfigColumn
    ] = camel as ShareableColumnId;
    return acc;
  },
  {} as Record<
    AffiliatePortalViewConfigKpi | AffiliatePortalViewConfigColumn,
    ShareableColumnId
  >,
);

// Inverse of `mapPortalViewConfigToPayload`: flattens the BE's
// `kpi_cards`/`table_columns` back into a single ShareableColumnId list.
// Falls back to DEFAULT_SELECTED_COLUMNS for legacy affiliates without the field.
export const mapPortalViewConfigFromResponse = (
  config: AffiliatePortalViewConfig | null | undefined,
): ShareableColumnId[] => {
  if (!config) return [...DEFAULT_SELECTED_COLUMNS];
  return [...config.kpi_cards, ...config.table_columns]
    .map((id) => KPI_ID_FROM_PORTAL_VIEW_CONFIG[id])
    .filter((id): id is ShareableColumnId => id !== undefined);
};

export const mapPromoCodesToPayload = (
  codes: ReadonlyArray<CreatePromoCodeFormValues>,
): AffiliatePromoCodePayload[] =>
  codes.map((code) => {
    const createAppsOnAirLink = code.createAppsOnAirLink !== false;
    return {
      promo_code: buildPromoCodeSlug(code.name),
      promo_name: trimOrNull(code.name),
      create_appsonair_link: createAppsOnAirLink,
      applink_name: createAppsOnAirLink ? trimOrNull(code.appLinkName) : null,
      deep_link_url: createAppsOnAirLink ? trimOrNull(code.deepLinkUrl) : null,
      ios_behavior: createAppsOnAirLink
        ? mapLinkBehavior(code.iosLinkBehavior)
        : null,
      android_behavior: createAppsOnAirLink
        ? mapLinkBehavior(code.androidLinkBehavior)
        : null,
      ios_fallback_url: mapFallbackUrl(
        createAppsOnAirLink,
        code.iosIfNotInstalled,
        code.iosCustomUrl,
      ),
      android_fallback_url: mapFallbackUrl(
        createAppsOnAirLink,
        code.androidIfNotInstalled,
        code.androidCustomUrl,
      ),
      social_preview_title: createAppsOnAirLink
        ? trimOrNull(code.previewTitle)
        : null,
      social_preview_description: createAppsOnAirLink
        ? trimOrNull(code.previewDescription)
        : null,
      social_preview_image_url: createAppsOnAirLink
        ? trimOrNull(code.previewImageUrl)
        : null,
    };
  });
